import { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('fpa_token');
    const savedUser = localStorage.getItem('fpa_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      authApi.getMe()
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('fpa_token');
          localStorage.removeItem('fpa_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { access_token, user: userData, must_change_password } = response.data;
    localStorage.setItem('fpa_token', access_token);
    localStorage.setItem('fpa_user', JSON.stringify(userData));
    setUser(userData);
    return { ...userData, must_change_password };
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('fpa_token', token);
    localStorage.setItem('fpa_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('fpa_token');
    localStorage.removeItem('fpa_user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('fpa_user', JSON.stringify(updatedUser));
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const hasPermission = (permission) => {
    if (user?.role === 'admin') return true;
    return user?.permissions?.includes(permission) || false;
  };

  const canApprove = () => {
    if (user?.role === 'admin') return true;
    return user?.permissions?.includes('approve') || false;
  };

  const getUserBuId = () => {
    return user?.business_unit_id || null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login,
      loginWithToken,
      logout, 
      updateUser,
      loading, 
      isAdmin, 
      hasPermission,
      canApprove,
      getUserBuId 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
